import React from 'react';

import Slider from '../../library/slider/slider';
import Tooltip from '../../library/tooltip/tooltip';
import Typography from '../../library/typography/typography';
import ClockRegularIcon from '../../shared/icons/clock-regular-icon';
import MilesPerYearInput from '../miles-per-year-input/miles-per-year-input';
import TireLifeCounter from '../tire-life-counter/tire-life-counter';

import {
  useAemContent,
  useTireLifeInfo,
  useTreadwellPreferences
} from '../../shared/hooks/hooks';

import { isWinterTire } from '../../shared/utils/product/product';
import {
  calculateLifeExpectancy,
  calculateWinterSeasons
} from '../../shared/utils/treadwell/treadwell';

import { Maybe, ProductData } from '../../shared/types/graphql-types';
import {
  AEM_CONTENT_IDS,
  AEM_CONTENT_PAGE_IDS
} from '../../shared/constants/cms';

import styles from './tire-life-span-info.scss';

interface TireLifeSpanInfoProps {
  max: number;
  min: number;
  months?: number;
  product?: ProductData;
  showTooltip?: boolean;
  winterDaysPerYear?: Maybe<number>;
  years?: number;
}

function TireLifeSpanInfo(props: TireLifeSpanInfoProps) {
  const {
    max,
    min,
    months,
    product,
    showTooltip = false,
    winterDaysPerYear = 0,
    years
  } = props;

  const {
    miles,
    milesInputError,
    onMilesInputBlur,
    onMilesInputChange,
    setMiles
  } = useTireLifeInfo();
  const { treadwellPreferences } = useTreadwellPreferences();

  const isWinterTreadwellFlow = Boolean(treadwellPreferences?.showWinterTires);

  const { mileage } = product?.recommendationData || product?.tdsData || {};
  const { aemTooltipsContent } = useAemContent(
    AEM_CONTENT_PAGE_IDS.treadwellPdp
  );
  const winterSeasonContent =
    aemTooltipsContent?.[AEM_CONTENT_IDS.winterSeason];
  const yearsAndMonthsContent =
    aemTooltipsContent?.[AEM_CONTENT_IDS.yearsAndMonths];

  const { tireLifeMonths, tireLifeYears } = calculateLifeExpectancy(
    product,
    15 * 1000,
    miles * 1000,
    months,
    years
  );

  const winterSeasons = calculateWinterSeasons({
    medianTireLifeEstimate: mileage?.median || 0,
    milesDrivenPerYear: miles * 1000,
    winterDaysPerYear: Number(winterDaysPerYear)
  });

  const isWinterData =
    isWinterTire(product) &&
    isWinterTreadwellFlow &&
    Number(winterDaysPerYear) > 0 &&
    winterSeasons > 0;

  return (
    <div className={styles['container']} data-testid="tire-life-span-info">
      <div className={styles['title']}>
        <ClockRegularIcon className={styles['icon']} />
        <Typography weight="bold">Tire Lifespan</Typography>
        {showTooltip && (
          <Tooltip
            className={styles['tooltip']}
            content={
              isWinterData
                ? `${winterSeasonContent?.tipText} ${treadwellPreferences?.zipCodeValue}`
                : yearsAndMonthsContent?.tipText
            }
          />
        )}
      </div>
      <div className={styles['slider']}>
        <Slider
          markerPrefix={`${min}K`}
          markerSuffix={`${max}K`}
          max={max}
          min={min}
          onChange={e => setMiles(e)}
          showTooltip
          value={miles}
        />
      </div>
      <MilesPerYearInput
        className={styles['miles-input']}
        defaultValue={miles}
        error={milesInputError}
        label="Slide above or type in your miles driven per year"
        max={max}
        min={min}
        onBlur={onMilesInputBlur}
        onChange={onMilesInputChange}
        value={miles}
      />
      {isWinterData ? (
        <div className={styles['winter-container']}>
          <TireLifeCounter countTo={winterSeasons} isWholeNum start>
            {({ value }) => <span className={styles['value']}>{value}</span>}
          </TireLifeCounter>
          <span className={styles['label']}>winter seasons</span>
        </div>
      ) : (
        <div className={styles['year-month-container']}>
          {typeof tireLifeYears === 'number' && (
            <div>
              <TireLifeCounter countTo={tireLifeYears} isWholeNum start>
                {({ value }) => (
                  <span className={styles['value']}>{value}</span>
                )}
              </TireLifeCounter>
              <span className={styles['label']}>years</span>
            </div>
          )}

          <div>
            {typeof tireLifeMonths === 'number' ? (
              <TireLifeCounter countTo={tireLifeMonths} isWholeNum start>
                {({ value }) => (
                  <span className={styles['value']}>{value}</span>
                )}
              </TireLifeCounter>
            ) : (
              <span className={styles['na']}>N/A</span>
            )}
            <span className={styles['label']}>months</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default TireLifeSpanInfo;
